import { useEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowRight, ArrowUpRight, Asterisk, Pause, Play } from 'lucide-react';
import type { Wallpaper } from '../types';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { ArtMedia } from './ArtMedia';
import { screenDate } from '../lib/screenDate';

const covers = [
  { id: 'molten', title: '熔金时刻', mood: '热烈一点', note: '把落日的最后一束光，留在掌心。' },
  { id: 'verdant', title: '浮光之间', mood: '自在一点', note: '像一片深林，轻轻呼吸。' },
  { id: 'azure', title: '静谧之蓝', mood: '安静一点', note: '让思绪，漂浮在一片蓝里。' },
];

export function EditorialHero({
  items,
  onDiscover,
  onOpen,
  suspended,
}: {
  items: Wallpaper[];
  suspended: boolean;
  onDiscover: () => void;
  onOpen: (item: Wallpaper) => void;
}) {
  const [cover, setCover] = useState(0);
  const stage = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const hero = useRef<HTMLElement>(null);
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [visible, setVisible] = useState(false);
  const [tabVisible, setTabVisible] = useState(() => document.visibilityState === 'visible');
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting && entry.intersectionRatio >= 0.25),
      { threshold: 0.25 },
    );
    observer.observe(hero.current!);
    const updateVisibility = () => setTabVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', updateVisibility);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', updateVisibility);
    };
  }, []);
  const rotating = !reduced && !hovered && !focused && visible && tabVisible && !suspended;
  useEffect(() => {
    if (!rotating) return;
    const timer = window.setInterval(() => setCover((index) => (index + 1) % covers.length), 6000);
    return () => window.clearInterval(timer);
  }, [rotating]);
  const current = covers[cover];
  const product = items.find((item) => item.id === current.id);
  function resetPosition() {
    stage.current?.style.setProperty('--pointer-x', '0px');
    stage.current?.style.setProperty('--pointer-y', '0px');
  }
  useEffect(() => {
    if (reduced) resetPosition();
  }, [reduced]);
  return (
    <section
      ref={hero}
      className={`exhibition-hero cover-${current.id}`}
      aria-label="本期封面作品"
      aria-roledescription="轮播"
      data-rotating={rotating}
    >
      <div className="cover-atmosphere" aria-hidden="true">
        {covers.map((item, index) => (
          <img
            key={item.id}
            className={cover === index ? 'is-current' : ''}
            src={`/art/${item.id}.jpg`}
            alt=""
          />
        ))}
        <span />
      </div>
      <div className="cover-copy">
        <p className="cover-eyebrow">为每一次点亮而作</p>
        <h1>
          把片刻心动，
          <br />
          <em>留在日常。</em>
        </h1>
        <p className="cover-description">
          从一束光、一片山海，
          <br />
          找到与你同频的画面。
        </p>
        <button className="button button-acid" onClick={onDiscover}>
          发现你的壁纸 <ArrowUpRight size={20} />
        </button>
      </div>
      <div
        className="cover-stage"
        ref={stage}
        onPointerMove={(event) => {
          if (reduced || event.pointerType !== 'mouse') return;
          const bounds = event.currentTarget.getBoundingClientRect();
          stage.current?.style.setProperty(
            '--pointer-x',
            `${((event.clientX - bounds.left) / bounds.width - 0.5) * 13}px`,
          );
          stage.current?.style.setProperty(
            '--pointer-y',
            `${((event.clientY - bounds.top) / bounds.height - 0.5) * 10}px`,
          );
        }}
        onPointerLeave={resetPosition}
      >
        <div className="stage-orbit" aria-hidden="true" />
        <span className="edition-seal" aria-hidden="true">
          <Asterisk size={42} strokeWidth={1.3} />
        </span>
        <button
          className="cover-object"
          onPointerEnter={(event) => {
            if (event.pointerType === 'mouse') setHovered(true);
          }}
          onPointerLeave={() => setHovered(false)}
          onFocus={(event) => setFocused(event.currentTarget.matches(':focus-visible'))}
          onBlur={() => setFocused(false)}
          onClick={() => product && onOpen(product)}
          disabled={!product}
          aria-label={`查看封面作品：${current.title}`}
        >
          {covers.map((item, index) => (
            <img
              key={item.id}
              className={cover === index ? 'is-current' : ''}
              src={`/art/${item.id}.jpg`}
              alt=""
              fetchPriority={index === 0 ? 'high' : 'auto'}
            />
          ))}
          <span className="object-cap">{screenDate.zh}</span>
          <span className="object-time">9:41</span>
          <span className="object-home" />
          <span className="object-view">
            <ArrowUpRight size={24} />
            <span>查看作品</span>
          </span>
        </button>
        <div className="cover-caption">
          <div className="cover-art-label">
            <strong key={current.id}>{current.title}</strong>
            <p>{current.note}</p>
          </div>
        </div>
      </div>
      <div className="cover-footer">
        <div className="cover-edition">
          <span className="cover-edition-number">
            {String(cover + 1).padStart(2, '0')}
            <small> / 03</small>
          </span>
          <div>
            <span className="cover-edition-label">本期灵感</span>
            <span className="cover-mood" key={current.id}>
              {current.mood}
            </span>
          </div>
        </div>
        <div className="cover-footer-line" aria-hidden="true">
          <i />
          <i />
          <i className="cover-line-end" />
        </div>
        <a
          className="cover-scroll"
          href="#discover"
          onClick={(event) => {
            event.preventDefault();
            onDiscover();
          }}
        >
          <span>
            向下探索<small>发现下一份心动</small>
          </span>
          <span className="cover-scroll-arrow">
            <ArrowDown size={21} />
          </span>
        </a>
      </div>
    </section>
  );
}

export function MotionStudy({
  item,
  onExplore,
  onOpen,
}: {
  item?: Wallpaper;
  onExplore: () => void;
  onOpen: (item: Wallpaper) => void;
}) {
  const reduced = useReducedMotion();
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    if (reduced) setPlaying(false);
  }, [reduced]);
  if (!item) return null;
  return (
    <section className="motion-study page-width" aria-label="动态作品展区">
      <div className={`motion-canvas ${playing ? 'is-playing' : ''}`}>
        <ArtMedia item={item} motion={playing} />
        <div className="motion-grain" />
        <span className="motion-overline">
          <span className="status-dot" />
          {playing ? '正在播放' : '轻触播放，感受光的流动'}
        </span>
        <span className="motion-big-type" aria-hidden="true">
          in
          <br />
          <em>motion.</em>
        </span>
        <button
          className="motion-play"
          onClick={() => setPlaying(!playing)}
          aria-label={playing ? '暂停展区动态' : '播放展区动态'}
          aria-pressed={playing}
        >
          {playing ? <Pause size={23} /> : <Play size={23} fill="currentColor" />}
        </button>
        <div className="motion-timeline">
          <span>循环预览</span>
          <div>
            <i />
          </div>
          <span>4 秒</span>
        </div>
      </div>
      <div className="motion-copy">
        <div className="section-kicker">动态壁纸</div>
        <span className="motion-mark" aria-hidden="true">
          ↝
        </span>
        <h2>
          让光，
          <br />
          慢一点。
        </h2>
        <p>
          有些美，不必静止。
          <br />
          把流动的光与呼吸的色彩，
          <br />
          带进每一次点亮。
        </p>
        <button className="text-button" onClick={() => onOpen(item)}>
          走进「静谧之蓝」
          <ArrowUpRight size={19} />
        </button>
        <button className="motion-all" onClick={onExplore}>
          探索全部动态作品 <ArrowRight size={15} />
        </button>
        <div className="motion-tech">
          <span>
            <strong>4</strong>秒循环
          </span>
          <span>
            <strong>30</strong>帧 / 秒
          </span>
          <span>
            <strong>HD</strong>高清 MP4
          </span>
        </div>
        <p className="motion-disclaimer">含 MP4 视频和静态原图，不含原生 Live Photo。</p>
      </div>
    </section>
  );
}

const stories = [
  {
    title: '光的形状',
    english: 'SHAPES\nOF LIGHT.',
    description: '让光，自由一点。',
    id: 'rouge',
    tone: 'light',
  },
  {
    title: '向野而生',
    english: 'BACK TO\nTHE WILD.',
    description: '向外走，也向内看。',
    id: 'forest',
    tone: 'wild',
  },
  {
    title: '留白练习',
    english: 'LESS,\nBUT MORE.',
    description: '留一点空，给自己。',
    id: 'silence',
    tone: 'quiet',
  },
];
export function Stories({
  items,
  onSelect,
}: {
  items: Wallpaper[];
  onSelect: (name: string) => void;
}) {
  return (
    <section className="collections-section page-width" id="collections">
      <div className="section-heading">
        <div>
          <div className="section-kicker">灵感合集</div>
          <h2>一种心情，一个世界。</h2>
        </div>
        <p>
          不只是相似的作品，
          <br />
          也是同一频率的喜欢。
        </p>
      </div>
      <div className="collection-grid">
        {stories.map((story) => (
          <button
            className={`collection-card collection-${story.tone}`}
            key={story.title}
            onClick={() => onSelect(story.title)}
          >
            <img className="story-image" src={`/art/${story.id}.jpg`} alt="" loading="lazy" />
            <span className="story-shade" />
            <span className="story-topline">
              {items.filter((item) => item.collection === story.title).length} 幅作品
            </span>
            <span className="story-english">{story.english}</span>
            <div className="collection-info">
              <div>
                <h3>{story.title}</h3>
                <p>{story.description}</p>
              </div>
              <span className="story-arrow">
                <ArrowUpRight size={24} />
              </span>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
export function Manifesto({ onAbout }: { onAbout: () => void }) {
  return (
    <section className="manifesto">
      <div className="manifesto-inner page-width">
        <div className="manifesto-meta">
          <span>关于幕间</span>
          <span>小屏幕里的，不止是像素。</span>
        </div>
        <div className="manifesto-main">
          <h2>
            不必向世界解释。
            <br />
            喜欢，<span>就够了。</span>
          </h2>
          <div className="manifesto-aside">
            <span className="manifesto-flower" aria-hidden="true">
              ✳
            </span>
            <p>
              设计不必遥远。
              <br />
              它就在你的口袋里，
              <br />
              在每一天，在每一屏。
            </p>
            <button className="text-button" onClick={onAbout}>
              关于幕间 <ArrowUpRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
